import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

interface SlackConfig {
  webhook_url: string
  channel: string
  enabled: boolean
  events: string[]
}

interface SaveSlackRequest {
  webhook_url: string
  channel: string
  enabled: boolean
  events: string[]
}

interface SlackTestResult {
  success: boolean
  error?: string
}

interface NotificationThreshold {
  id: string
  metric: string
  operator: 'gt' | 'lt' | 'gte' | 'lte'
  value: number
  enabled: boolean
}

interface ThresholdsResponse {
  data: NotificationThreshold[]
}

interface SaveThresholdsRequest {
  thresholds: Omit<NotificationThreshold, 'id'>[]
}

interface NotificationHistoryEntry {
  id: string
  type: string
  channel: 'slack' | 'email'
  message: string
  sent_at: string
}

interface NotificationHistoryResponse {
  data: NotificationHistoryEntry[]
  total: number
  page: number
  per_page: number
}

const SLACK_KEY = ['notifications', 'slack'] as const
const THRESHOLDS_KEY = ['notifications', 'thresholds'] as const
const HISTORY_KEY = ['notifications', 'history'] as const

export function useSlackConfig(): ReturnType<typeof useQuery<SlackConfig>> {
  return useQuery({
    queryKey: SLACK_KEY,
    queryFn: () => apiClient.get<SlackConfig>(endpoints.notifications.getSlack),
  })
}

export function useSaveSlackConfig(): ReturnType<
  typeof useMutation<SlackConfig, unknown, SaveSlackRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveSlackRequest) =>
      apiClient.put<SlackConfig>(endpoints.notifications.saveSlack, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: SLACK_KEY }),
  })
}

export function useTestSlack(): ReturnType<typeof useMutation<SlackTestResult, unknown, void>> {
  return useMutation({
    mutationFn: () => apiClient.post<SlackTestResult>(endpoints.notifications.testSlack, {}),
  })
}

export function useNotificationThresholds(): ReturnType<typeof useQuery<ThresholdsResponse>> {
  return useQuery({
    queryKey: THRESHOLDS_KEY,
    queryFn: () => apiClient.get<ThresholdsResponse>(endpoints.notifications.getThresholds),
  })
}

export function useSaveNotificationThresholds(): ReturnType<
  typeof useMutation<ThresholdsResponse, unknown, SaveThresholdsRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveThresholdsRequest) =>
      apiClient.put<ThresholdsResponse>(endpoints.notifications.saveThresholds, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: THRESHOLDS_KEY }),
  })
}

export function useNotificationHistory(params?: {
  page?: number
  per_page?: number
}): ReturnType<typeof useQuery<NotificationHistoryResponse>> {
  return useQuery({
    queryKey: [...HISTORY_KEY, params],
    queryFn: () =>
      apiClient.get<NotificationHistoryResponse>(endpoints.notifications.getHistory(params)),
  })
}

export type {
  NotificationHistoryEntry,
  NotificationHistoryResponse,
  NotificationThreshold,
  SaveSlackRequest,
  SaveThresholdsRequest,
  SlackConfig,
  SlackTestResult,
  ThresholdsResponse,
}
